/**
 * WorldStatusPanel - Presenter Component (ADR-034)
 *
 * Pure presenter: receives all pre-computed data via props.
 * ZERO store access. ZERO API calls. All derived data computed in Container.
 */

import React from 'react';
import { Card, Descriptions, List, Tag, Tooltip, Typography } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { DailyAlmanac, Moon, WeatherForecast, WeatherEvent } from '../../../../types/types';

const { Title, Text } = Typography;

/** Moon data enriched with computed phase information */
export type MoonWithPhase = Moon & { phase: { name: string; illumination: number } };

export interface WorldStatusPanelProps {
  campaignDate: Date | null;
  todayAlmanac: DailyAlmanac | undefined;
  todayForecast: WeatherForecast | null | undefined;
  activeWeatherEvents: WeatherEvent[];
  moonPhases: MoonWithPhase[];
}

const WorldStatusPanel: React.FC<WorldStatusPanelProps> = ({
  campaignDate,
  todayAlmanac,
  todayForecast,
  activeWeatherEvents,
  moonPhases,
}) => {
  return (
    <>
    {!campaignDate && <Card title="World Status" style={{ height: '100%' }}><Text type="secondary">No date available.</Text></Card>}
    {campaignDate && (
    <Card title="World Status" style={{ height: '100%' }}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label={<><CalendarOutlined /> Date</>}>
          {dayjs(campaignDate).format('dddd, MMMM D, YYYY')}
        </Descriptions.Item>
        <Descriptions.Item label={<><ClockCircleOutlined /> Time</>}>
          {dayjs(campaignDate).format('HH:mm A')}
        </Descriptions.Item>
      </Descriptions>

      {todayAlmanac && (
        <Descriptions column={2} bordered size="small" style={{ marginTop: 16 }}>
          <Descriptions.Item label={<><SunOutlined /> Sunrise</>}>{dayjs(todayAlmanac.sunriseTime).format('HH:mm')}</Descriptions.Item>
          <Descriptions.Item label={<><MoonOutlined /> Sunset</>}>{dayjs(todayAlmanac.sunsetTime).format('HH:mm')}</Descriptions.Item>
          <Descriptions.Item label={<>High Temp</>}>{todayAlmanac.tempHigh}°</Descriptions.Item>
          <Descriptions.Item label={<>Low Temp</>}>{todayAlmanac.tempLow}°</Descriptions.Item>
        </Descriptions>
      )}

      {todayForecast && (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>Forecast</Title>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Condition">
              <Tag icon={todayForecast.weatherCondition?.icon ? <i className={`fa fa-${todayForecast.weatherCondition.icon}`} /> : null}>
                {todayForecast.weatherCondition?.name ?? 'Unknown'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Temperature">{todayForecast.temperature ?? 'N/A'}°</Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {activeWeatherEvents.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>Active Events</Title>
          <List
            size="small"
            dataSource={activeWeatherEvents}
            renderItem={(event: WeatherEvent) => (
              <List.Item>
                <List.Item.Meta title={<Tooltip title={event.description}><Text>{event.name}</Text></Tooltip>} description={<Text type="secondary">{event.effect}</Text>} />
              </List.Item>
            )}
          />
        </div>
      )}

      {moonPhases.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>Celestial Bodies</Title>
          <List
            size="small"
            dataSource={moonPhases}
            renderItem={(moon: MoonWithPhase) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<div style={{ width: 18, height: 18, backgroundColor: moon.color ?? '#ccc', borderRadius: '50%' }} />}
                  title={moon.name}
                  description={`${moon.phase.name} (${Math.round(moon.phase.illumination * 100)}%)`}
                />
              </List.Item>
            )}
          />
        </div>
      )}
    </Card>
    )}
    </>
  );
};

export default WorldStatusPanel;